import React, { useEffect, useRef, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Card, CardBody, Col } from 'reactstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { GET_CARD_SUMMARY, GET_FILTERS_LIST } from '../../../../store/actions';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface RootState {
  GetFiltersList?: {
    data?: any;
    loading?: boolean;
  };
}

const BarChart = ({ cardDetails, cardData }) => {
  const dispatch = useDispatch();

  const { filterAPiOptions } = useSelector((state: RootState) => ({
    filterAPiOptions: state.GetFiltersList.data,
  }));

  const [filter, setFilter] = useState({ start_date: moment().subtract(7, 'days').toISOString(), end_date: moment().toISOString(), region: null });
  const flatpickrRef = useRef<any>(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [regionOption, setRegionOption] = useState([]);

  useEffect(() => {
    if (cardDetails) {
      dispatch({
        type: GET_CARD_SUMMARY,
        payload: {
          cards: [cardDetails],
          ...filter,
          region: filter?.region?.value ?? '',
        },
      });
      dispatch({
        type: GET_FILTERS_LIST,
        payload: {
          urlParams: {
            card_name: cardDetails?.name,
            start_date: filter?.start_date,
            end_date: filter?.end_date,
          },
        },
      });
    }
  }, [cardDetails, filter]);

  useEffect(() => {
    if (cardData) {
      setSelectedItems(cardData?.[cardDetails?.name]?.data?.data?.distributions);
    }
  }, [cardData]);

  useEffect(() => {
    if (filterAPiOptions) {
      const regionOptions = filterAPiOptions?.results?.[0]?.data?.map(item => ({
        value: item?.value,
        label: item?.value,
      }));
      setRegionOption(regionOptions);
    }
  }, [filterAPiOptions]);

  const data = {
    labels: selectedItems?.map(item => item?.vendorName),
    datasets: [
      {
        label: 'Values',
        data: selectedItems?.map(item => item?.value),
        backgroundColor: selectedItems?.map(item => item?.color),
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        labels: {
          generateLabels: chart => {
            return selectedItems?.map((item, index) => ({
              text: `${item?.vendorName} - ${item?.value}`,
              fillStyle: item.color,
              strokeStyle: item.color,
              index,
            }));
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        color: '#ffffff', // white text
        anchor: 'end',
        align: 'start',
        font: {
          weight: 'bold',
        },
        formatter: (value: number) => value,
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 5, // 👈 Change this value to your desired font size
            },
          },
        },
      },
    },
  };

  useEffect(() => {
    if (flatpickrRef.current) {
      const flatpickrInstance = flatpickrRef.current.flatpickr;

      flatpickrInstance.config.onOpen.push(() => {
        const calendarContainer = flatpickrInstance.calendarContainer;

        if (!calendarContainer.querySelector('.custom-options')) {
          const customOptions = document.createElement('div');
          customOptions.className = 'custom-options';
          Object.assign(customOptions.style, {
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            margin: '0', // Remove extra spacing
            padding: '10px',
            backgroundColor: '#FFFFFF', // Background for better visibility
            position: 'absolute', // Allow positioning next to the calendar
            top: '0', // Align with the calendar's top
            right: 'calc(100% + 1px)', // Position it to the right of the calendar
            zIndex: '9999', // Ensure it appears above other elements
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '5px',
          });

          const options = ['Today', 'Last 7 Days', 'Last 14 Days', 'Custom Range'];

          options.forEach(option => {
            const button = document.createElement('button');
            Object.assign(button.style, {
              padding: '11px 10px',
              fontSize: '0.8rem',
              margin: '10px 0',
              backgroundColor: '#3F5189',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              width: '122px',
            });

            button.textContent = option;

            button.onmouseenter = () => (button.style.backgroundColor = '#5A6FA1');
            button.onmouseleave = () => (button.style.backgroundColor = '#3F5189');

            button.addEventListener('click', () => handleCustomRange(option, flatpickrInstance));
            customOptions.appendChild(button);
          });

          calendarContainer.parentNode.style.position = 'relative'; // Ensure parent allows positioning
          calendarContainer.appendChild(customOptions);
        }
      });
    }
  }, []);

  const handleCustomRange = (option, flatpickrInstance) => {
    let start_date = null;
    let end_date = null;

    switch (option) {
      case 'Today':
        start_date = end_date = new Date();
        break;
      case 'Last 7 Days':
        start_date = new Date(new Date().setDate(new Date().getDate() - 7));
        end_date = new Date();
        break;
      case 'Last 14 Days':
        start_date = new Date(new Date().setDate(new Date().getDate() - 14));
        end_date = new Date();
        break;
      default:
        return;
    }
    // Set selected range and close calendar
    let updatedState = null;
    updatedState = {
      ...filter,
      start_date: moment(start_date).toISOString(),
      end_date: moment(end_date).toISOString(),
    };
    setFilter(updatedState);
    flatpickrInstance.close();
    // const filtersObject = appliedFilters.reduce((acc, curr) => {
    //   acc[curr.key] = curr.value;
    //   return acc;
    // }, {});
    // props?.filterHandler({ ...updatedState, ...filtersObject });
  };

  const datePickHandler = (flag, date) => {
    let updatedState = null;
    if (date[0]) {
      const start = new Date(date[0]);
      const maxRange = new Date(start);
      maxRange.setDate(start.getDate() + 60);

      // Set the Flatpickr range restrictions dynamically
      flatpickrRef.current.flatpickr.set('minDate', start);
      flatpickrRef.current.flatpickr.set('maxDate', maxRange);
    }
    switch (flag) {
      case 'date':
        if (date[0] && date[1]) {
          updatedState = {
            ...filter,
            start_date: moment(new Date(date[0])).toISOString(),
            end_date: moment(new Date(date[1])).toISOString(),
          };
          setFilter(updatedState);
        }
        break;
    }
  };
  return (
    <Col xl={6} md={12} lg={6} className="mb-2" style={{ borderRadius: '10px' }}>
      <Card>
        <div className="d-flex justify-content-between m-4">
          <h4 className="card-title mb-0 flex-grow-1 fw-semibold">Vendor Wise Booking Distribution</h4>
          <div className="d-flex gap-2 align-items-center">
            <Flatpickr
              ref={flatpickrRef}
              className="form-control"
              onChange={date => datePickHandler(`date`, date)}
              value={filter?.start_date && filter?.end_date ? [new Date(filter.start_date), new Date(filter.end_date)] : []}
              placeholder="Select Range"
              options={{
                mode: 'range',
                dateFormat: 'Y-m-d',
              }}
              style={{ width: '201px' }}
            />
            <Select
              options={regionOption}
              value={filter?.region}
              placeholder="Select Region"
              onChange={data => {
                setFilter(prev => ({
                  ...prev,
                  region: data,
                }));
              }}
              styles={{
                control: provided => ({
                  ...provided,
                  width: '150px', // Set custom width
                }),
              }}
            />
          </div>
        </div>

        <CardBody style={{ maxHeight: '420px', position: 'relative', textAlign: 'center', height: '402px' }}>
          <div style={{ height: '300px', position: 'relative' }}>
            <Bar data={data} options={options} />
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default BarChart;
